import { useEffect, useState } from 'react';
/**
 * v2 workspace — tab shell for the v2 model.
 *
 * Self-contained rather than wired into v1's ActiveView switch: v1 is live
 * production, and the two use different stores.
 *
 * The analysis tabs — Annual Summary, What-If, Sensitivity — are v1's own
 * views, reused unchanged. The backend computes those three blocks with the
 * same models for both versions, so the views are already version-agnostic;
 * AnalysisSourceProvider is what points them at the v2 store instead of v1's.
 *
 * The 'pending' flag is kept for a tab added ahead of its view: such a tab
 * should be listed and DISABLED rather than hidden, so the gap reads as 'not
 * built yet' instead of 'data missing'.
 */

import { useChatStore } from '../../store/chat';
import { Button } from './ui/Button';
import { SaveState } from './ui/SaveState';
import { WorkspaceShell } from './ui/WorkspaceShell';
import { WorkingIndicator } from '../../components/WorkingIndicator';
import { ChatPanel } from '../../components/ChatPanel';
import { SensitivityView } from '../SensitivityView';
import { WhatIfView } from '../WhatIfView';
import { AnalysisSourceProvider } from '../../components/financial/AnalysisSourceContext';
import { BacktestViewV2 } from './valuation/BacktestViewV2';
import { ForecastViewV2 } from './accounting/ForecastViewV2';
import { AiValuationViewV2 } from './valuation/AiValuationViewV2';
import { ValuationWhatIfV2 } from './valuation/ValuationWhatIfV2';
import { BalanceSheetViewV2 } from './accounting/BalanceSheetViewV2';
import { ChartsViewV2 } from './accounting/ChartsViewV2';
import { FormulasPanelV2, type FormulaGroup } from './FormulasPanelV2';
import { FORMULAS, TAB_ORDER } from './accounting/formulas';
import { TAB_LABELS } from './formulaTypes';
import { VALUATION_FORMULAS } from './valuation/valuationFormulas';
import { OverviewViewV2 } from './accounting/OverviewViewV2';
import { AccountingSettingsV2 } from './accounting/AccountingSettingsV2';
import { ValuationSettingsV2 } from './valuation/ValuationSettingsV2';
import { VersionsViewV2 } from './VersionsViewV2';
import { CashflowViewV2 } from './accounting/CashflowViewV2';
import { EquityViewV2 } from './accounting/EquityViewV2';
import { CogsView } from './accounting/CogsView';
import { ControlAccountViewV2 } from './accounting/ControlAccountViewV2';
import { FixedAssetsView } from './accounting/FixedAssetsView';
import { IndirectCostsView } from './accounting/IndirectCostsView';
import { PnLViewV2 } from './accounting/PnLViewV2';
import { StaffingViewV2 } from './accounting/StaffingViewV2';
import { useFinancialModelV2Store } from '../../store/financial-model-v2';
import DisciplineSwitch from './DisciplineSwitch';
import { ValuationOverviewV2 } from './valuation/ValuationOverviewV2';
import type { Discipline, DisciplineId } from './disciplines';

interface Tab {
  id: string;
  label: string;
  pending?: boolean;
}

interface TabGroup {
  /**
   * What a discipline names to claim this group.
   *
   * Separate from the label because two disciplines each have a group called
   * "Analysis" holding different tabs. The filter used to match on the label, which
   * worked only while no two groups shared a name.
   */
  id: string;
  /** Shown above the tabs. Empty for the trailing admin group, which needs
      no label - nobody wonders what Versions and Settings are. */
  label: string;
  tabs: Tab[];
}

/**
 * Accounts and valuation are different kinds of work and are now shown as
 * such.
 *
 * Mike's workbook is an accounting model: eight of its nine active sheets are
 * double-entry bookkeeping, and the only valuation content in it is five
 * parameters on the Settings sheet. The valuation engine is this product's
 * own addition. Running the two together in one undifferentiated strip of
 * sixteen tabs hid that distinction from the user, and hid it from us: the
 * accounting half is checked line by line against the workbook, the valuation
 * half has no reference to check against at all.
 *
 * Within each group the order is unchanged - the three statements lead, in
 * the order they are read, and the schedules that feed them follow.
 */
const TAB_GROUPS: TabGroup[] = [
  {
    id: 'accounts',
    label: 'Accounts',
    tabs: [
      { id: 'overview', label: 'Overview' },
      { id: 'pnl', label: 'P&L' },
      { id: 'cashflow', label: 'Cashflow' },
      { id: 'balance-sheet', label: 'Balance Sheet' },
      { id: 'equity', label: 'Changes in Equity' },
      { id: 'cogs', label: 'COGS' },
      { id: 'indirect-costs', label: 'Indirect Costs' },
      { id: 'fixed-assets', label: 'Fixed Assets & Depn' },
      { id: 'staffing', label: 'Staffing' },
      { id: 'control-account', label: 'Control Account' },
      { id: 'charts', label: 'Charts' },
    ],
  },
  {
    // Accounting's own analysis. All four of these used to sit in the Valuation
    // group, where an accounting user could not reach them - and three of them have
    // no valuation reading at all: Sensitivity shocks a COGS ratio and an interest
    // rate, Backtest asks whether a forecast was right, and the forecast fits
    // posteriors for the accounting drivers. The fourth, What-If, scales revenue and
    // headcount. Every one is a statement question.
    id: 'accounting-analysis',
    label: 'Analysis',
    tabs: [
      { id: 'what-if', label: 'What-If' },
      { id: 'sensitivity', label: 'Sensitivity' },
      { id: 'forecast', label: 'AI Forecast' },
      { id: 'backtest', label: 'Backtest' },
    ],
  },
  {
    id: 'valuation',
    label: 'Valuation',
    tabs: [
      // First, because it is what the discipline is for. It used to be the right
      // hand column of the accounting overview; a discipline is chosen before any
      // of this now, so an accounting user has no reason to meet a discounted cash
      // flow.
      { id: 'valuation-overview', label: 'Valuation' },
    ],
  },
  {
    // Valuation's own analysis, moving the things a valuation argument is actually
    // about: the cost of capital, the terminal growth, the multiples, and the flows.
    // `ai-valuation` reports the enterprise value the forecast pipeline already
    // produced - the same run, not a second one - and proposes the assumptions.
    id: 'valuation-analysis',
    label: 'Analysis',
    tabs: [
      { id: 'valuation-what-if', label: 'What-If' },
      { id: 'ai-valuation', label: 'AI Valuation' },
    ],
  },
  {
    id: 'admin',
    label: '',
    tabs: [
      { id: 'versions', label: 'Versions' },
      { id: 'settings', label: 'Settings' },
    ],
  },
];

/**
 * The groups this discipline shows.
 *
 * Filtered rather than hidden with CSS: a tab that is not part of the current
 * discipline should not exist, which is what makes the separation structural. The
 * trailing admin group has an empty label and belongs to both - a model has one
 * set of Versions and Settings whichever discipline is looking at it.
 */
function groupsFor(discipline: Discipline): TabGroup[] {
  return TAB_GROUPS.filter((group) => discipline.tabGroups.includes(group.id));
}

/**
 * Which formulas the panel shows.
 *
 * Here rather than in the panel because this file is one of the two composition roots
 * allowed to know about both disciplines. The panel takes the answer as a prop.
 *
 * Accounting gets a tab per sheet, because nine sheets feed each other and the
 * interesting question is which one decides a line. The valuation gets ONE group: it has
 * a single path from finished statements to a number, and a tab strip of one would be
 * furniture.
 */
function formulaGroups(discipline: Discipline): FormulaGroup[] {
  if (discipline.id === 'valuation') {
    return [{ key: 'valuation', label: 'Valuation', sections: VALUATION_FORMULAS }];
  }
  return TAB_ORDER.map((key) => ({
    key,
    label: TAB_LABELS[key],
    sections: FORMULAS[key],
  }));
}

function ActiveV2View({
  sheet,
  discipline,
}: {
  sheet: string;
  discipline: Discipline;
}) {
  switch (sheet) {
    case 'valuation-overview':
      return <ValuationOverviewV2 />;
    case 'pnl':
      return <PnLViewV2 />;
    case 'cogs':
      return <CogsView />;
    case 'indirect-costs':
      return <IndirectCostsView />;
    case 'fixed-assets':
      return <FixedAssetsView />;
    case 'staffing':
      return <StaffingViewV2 />;
    case 'control-account':
      return <ControlAccountViewV2 />;
    case 'cashflow':
      return <CashflowViewV2 />;
    case 'balance-sheet':
      return <BalanceSheetViewV2 />;
    case 'equity':
      return <EquityViewV2 />;
    case 'overview':
      return <OverviewViewV2 />;
    case 'charts':
      return <ChartsViewV2 />;
    case 'what-if':
      return <WhatIfView />;
    case 'sensitivity':
      return <SensitivityView />;
    case 'forecast':
      return <ForecastViewV2 />;
    case 'ai-valuation':
      return <AiValuationViewV2 />;
    case 'valuation-what-if':
      return <ValuationWhatIfV2 />;
    case 'backtest':
      return <BacktestViewV2 />;
    case 'versions':
      return <VersionsViewV2 />;
    case 'settings':
      // The only tab whose page depends on the discipline. Both disciplines have
      // settings and a model has one set, but half of them cannot change what the
      // other discipline is looking at - see the two files.
      return discipline.id === 'valuation' ? (
        <ValuationSettingsV2 />
      ) : (
        <AccountingSettingsV2 />
      );
    default:
      return (
        <div className="p-8 text-sm text-gray-500">
          This tab is not built yet. The model computes it — see the export for
          the full statement.
        </div>
      );
  }
}

export function V2Workspace({
  discipline,
  onSwitchDiscipline,
}: {
  discipline: Discipline;
  onSwitchDiscipline: (id: DisciplineId) => void;
}) {
  // Before the early return below, so the hook order cannot change between
  // a loaded and an unloaded model.
  const [showFormulas, setShowFormulas] = useState(false);
  const model = useFinancialModelV2Store((s) => s.model);
  const activeSheet = useFinancialModelV2Store((s) => s.activeSheet);
  const setActiveSheet = useFinancialModelV2Store((s) => s.setActiveSheet);
  const exportModel = useFinancialModelV2Store((s) => s.exportModel);
  const updateSetting = useFinancialModelV2Store((s) => s.updateSetting);
  const isRecalculating = useFinancialModelV2Store((s) => s.isRecalculating);
  const isDirty = useFinancialModelV2Store((s) => s.isDirty);
  const flushPendingSave = useFinancialModelV2Store((s) => s.flushPendingSave);
  const chatOpen = useChatStore((s) => s.isOpen);
  const toggleChat = useChatStore((s) => s.togglePanel);
  const closeModel = useFinancialModelV2Store((s) => s.closeModel);
  const saveError = useFinancialModelV2Store((s) => s.saveError);

  const groups = groupsFor(discipline);

  // A sheet from the other discipline must not stay open when this one is
  // chosen: the store remembers the last sheet across sessions, so an accounting
  // user returning to a valuation project would otherwise land on the P&L.
  useEffect(() => {
    const allowed = groups.flatMap((group) => group.tabs).map((tab) => tab.id);
    if (!allowed.includes(activeSheet)) setActiveSheet(discipline.defaultTab);
  }, [activeSheet, discipline, groups, setActiveSheet]);

  if (!model) {
    return (
      <div className="p-8 text-sm text-gray-500">No model loaded.</div>
    );
  }

  return (
    <WorkspaceShell
      discipline={discipline}
      onBack={() => void closeModel()}
      title={model.name}
      subtitle={<>Version {model.version}</>}
      status={
        <>
          <SaveState isSaving={isRecalculating} isDirty={isDirty} />
          {isRecalculating && <WorkingIndicator label="Recalculating" />}
        </>
      }
      // Beside the model's name: switching discipline is a change of view on THIS
      // model, not navigation away from it.
      switcher={<DisciplineSwitch current={discipline} onSwitch={onSwitchDiscipline} />}
      groups={groups.map((group) => ({
        id: group.id,
        label: group.label,
        tabs: group.tabs.map((tab) => ({ id: tab.id, label: tab.label, disabled: tab.pending })),
      }))}
      active={activeSheet}
      onSelect={setActiveSheet}
      tools={
        <>
          <Button size="sm" variant={chatOpen ? 'secondary' : 'quiet'} onClick={toggleChat}>
            Ask AI
          </Button>
          {/* Next to the sheet, not buried in settings: the question it answers
              ("where does this number come from") is asked while looking at it. */}
          <Button size="sm" onClick={() => setShowFormulas(true)}>
            Formulas
          </Button>
          <Button size="sm" variant="secondary" onClick={exportModel}>
            Export Excel
          </Button>
        </>
      }
      // Shown here, not only inside the sheet tables, because Back refuses to leave
      // while an edit is unsaved. On a tab with no table the button would otherwise
      // look simply dead.
      banner={
        saveError && (
          <div className="shrink-0 border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">
            <strong>Not saved.</strong> {saveError}
          </div>
        )
      }
      aside={
        chatOpen && (
          <aside className="w-96 shrink-0 border-l border-gray-200 bg-white">
            <ChatPanel />
          </aside>
        )
      }
    >
      <AnalysisSourceProvider
        value={{
          settings: model.settings,
          annualSummary: model.computed.annualSummary,
          whatIf: model.computed.whatIf,
          sensitivity: model.computed.sensitivity,
          valuation: model.computed.valuation,
          isRecalculating,
          isDirty,
          updateSetting: (key, value) =>
            updateSetting(key as never, value as never),
          // v2 saves on a debounce, so this button is a 'do it now' rather
          // than the only way a change reaches the server.
          recalculate: flushPendingSave,
        }}
      >
        <ActiveV2View sheet={activeSheet} discipline={discipline} />
      </AnalysisSourceProvider>

      {/* A full-height overlay, not a panel inside the 384px sidebar. */}
      <FormulasPanelV2
        open={showFormulas}
        onClose={() => setShowFormulas(false)}
        groups={formulaGroups(discipline)}
        subtitle={
          discipline.id === 'valuation'
            ? 'Every step from finished statements to a value'
            : "The IAS accounting engine's rules, line by line, and the tabs each one reads"
        }
      />
    </WorkspaceShell>
  );
}
